/**
 * AnswerMarkdown — the chat's markdown renderer.
 *
 * The headline, text blocks and findings of an Answer Document render
 * through this. It renders markdown, nothing else: no fence is a widget any
 * more — widgets are blocks the AnswerRenderer walks. Entity links use the
 * trodo:// scheme and are opened by ChatPage's click delegation.
 */
import { Children, memo, useMemo } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import ChatMarkdownLink from "shared/components/chat/ChatMarkdownLink";
import { linkifyMarkdownChildren } from "shared/components/chat/chatMarkdownEntityLinks";



/**
 * Href sanitizer for answer markdown.
 *
 * react-markdown drops any protocol its default transform does not recognise,
 * and `trodo://` is not one of them — so every entity link the analyst wrote
 * arrived with href="" and rendered as inert text. Let our own scheme through
 * untouched and hand everything else to the default, which is what keeps
 * `javascript:` out.
 */
function urlTransformAllowingEntities(url) {
  return typeof url === "string"
    && (url.startsWith("trodo://entity/") || url.startsWith("trodo://cite/"))
    ? url
    : defaultUrlTransform(url);
}



/** Assistant bubbles: render markdown (headings, bold, tables, numbered lists). */
/**
 * One style sheet for every answer block. It was a <style> inside each
 * AnswerMarkdown: an answer with twelve prose blocks put the same rules in
 * the page twelve times, and each re-render rewrote them.
 */
const CHAT_MD_CSS = `
        /* === Trodo chat answer — typography (Inter, theme tokens) === */
        .trodo-chat-md {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 14.5px;
          line-height: 1.65;
          color: ${theme.textPrimary};
          text-align: left;
          word-break: break-word;
          min-width: 0;
          letter-spacing: -0.005em;
        }
        .trodo-chat-md p {
          margin: 0 0 12px 0;
        }
        .trodo-chat-md p:last-child { margin-bottom: 0; }

        /* Headings — quiet, structural, NOT loud */
        .trodo-chat-md h1, .trodo-chat-md h2, .trodo-chat-md h3, .trodo-chat-md h4 {
          font-family: 'Inter', sans-serif;
          color: ${theme.textPrimary};
          font-weight: 600;
          letter-spacing: -0.012em;
          margin: 20px 0 8px 0;
        }
        .trodo-chat-md h1 { font-size: 17px; }
        .trodo-chat-md h2 { font-size: 15.5px; }
        .trodo-chat-md h3 { font-size: 14.5px; }
        .trodo-chat-md h4 { font-size: 13.5px; color: ${theme.textSecondary}; font-weight: 500; text-transform: uppercase; letter-spacing: 0.04em; }

        /* Lists — clean, indented. list-style-type MUST be explicit because
           the global CSS reset zeroes it; without these, numbers + bullets
           never render and the structure looks like a wall of paragraphs. */
        .trodo-chat-md ol {
          list-style-type: decimal;
          margin: 8px 0 12px 0;
          padding-left: 24px;
        }
        .trodo-chat-md ul {
          list-style-type: disc;
          margin: 8px 0 12px 0;
          padding-left: 22px;
        }
        .trodo-chat-md ol ol { list-style-type: lower-alpha; }
        .trodo-chat-md ul ul { list-style-type: circle; }
        .trodo-chat-md li {
          margin: 4px 0;
          padding-left: 2px;
        }
        .trodo-chat-md li > p { margin: 0; }
        .trodo-chat-md ol > li::marker {
          color: ${theme.textSecondary};
          font-weight: 500;
          font-variant-numeric: tabular-nums;
        }
        .trodo-chat-md ul > li::marker {
          color: ${theme.textSecondary};
        }

        /* Emphasis — subtle, NOT shouting. Bold gets a touch more weight
           but stays the same color so it reads as semantic emphasis. */
        .trodo-chat-md strong {
          font-weight: 600;
          color: ${theme.textPrimary};
        }
        .trodo-chat-md em {
          font-style: italic;
          color: ${theme.textPrimary};
        }

        /* Inline code — soft themed chip for distinctIds / IDs / exact
           values. Slightly tinted background, NO border, monospace inset. */
        .trodo-chat-md code.trodo-chat-code-inline,
        .trodo-chat-md :not(pre) > code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 0.86em;
          padding: 1px 6px;
          border-radius: 4px;
          background: rgb(var(--trodo-primary-rgb) / 0.08);
          color: ${theme.textPrimary};
          font-weight: 500;
          line-height: 1.4;
          /* Short ids stay on one line; a quoted error message wraps instead
             of running off the edge of the answer (29 Oct). */
          white-space: normal;
          overflow-wrap: anywhere;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
        }

        /* Block code — neutral, well-spaced */
        .trodo-chat-md pre.trodo-chat-pre {
          margin: 12px 0;
          padding: 12px 14px;
          border: 1px solid ${theme.borderMuted};
          background: var(--surface-sunken, rgba(0,0,0,0.025));
          border-radius: 10px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12.5px;
          line-height: 1.55;
          color: ${theme.textPrimary};
          overflow-x: auto;
        }
        .trodo-chat-md pre.trodo-chat-pre code {
          background: transparent;
          padding: 0;
          font-size: inherit;
          color: inherit;
          white-space: pre;
        }

        /* In-app entity link — themed but quiet. Color only; underline on
           hover. Looks like a value, not a "click me!" button. */
        .trodo-chat-md a.trodo-chat-entity-link {
          color: var(--trodo-primary);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px dotted rgb(var(--trodo-primary-rgb) / 0.4);
          padding-bottom: 0;
          transition: border-color 120ms ease, background-color 120ms ease;
          cursor: pointer;
        }
        .trodo-chat-md a.trodo-chat-entity-link:hover {
          border-bottom: 1px solid var(--trodo-primary);
          background: rgb(var(--trodo-primary-rgb) / 0.06);
          border-radius: 3px;
          padding: 0 2px;
          margin: 0 -2px;
        }

        /* Entity TAG — ids referenced in prose. A run id inside a sentence is
           a thing you act on, so it gets a chip: monospaced (ids are read
           character by character), tinted, and visibly clickable. The plain
           link style above still applies to labelled links (emails, names)
           where a chip in mid-sentence would be noise. */
        .trodo-chat-md a.trodo-chat-entity-tag {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 0.84em;
          font-weight: 500;
          padding: 1px 6px;
          border-radius: 5px;
          border: 1px solid rgb(var(--trodo-primary-rgb) / 0.28);
          background: rgb(var(--trodo-primary-rgb) / 0.08);
          color: var(--trodo-primary);
          white-space: nowrap;
          vertical-align: baseline;
        }
        .trodo-chat-md a.trodo-chat-entity-tag:hover {
          background: rgb(var(--trodo-primary-rgb) / 0.16);
          border-color: var(--trodo-primary);
          border-bottom: 1px solid var(--trodo-primary);
          padding: 1px 6px;
          margin: 0;
        }

        /* Tables — bordered, readable, themed */
        /* One line per row; a wide table scrolls sideways inside the bubble
           rather than wrapping its cells (Akshit, 3 Sep). */
        .trodo-chat-md table {
          display: block;
          width: max-content;
          max-width: 100%;
          overflow-x: auto;
          border-collapse: collapse;
          margin: 12px 0;
          font-family: Inter, sans-serif;
          background: var(--surface-base);
          border: 1px solid var(--border-default);
          border-radius: 6px;
        }
        /* Same table as Traces (dataTableStyles): h-8 rows, micro-label
           header with no fill over a border-default hairline, 12px cells,
           border-subtle row hairlines, none under the last row. */
        .trodo-chat-md tr { height: 32px; }
        .trodo-chat-md th, .trodo-chat-md td {
          padding: 0 12px;
          text-align: left;
          vertical-align: middle;
          white-space: nowrap;
        }
        .trodo-chat-md th {
          height: 32px;
          font-size: 10.5px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-muted);
          border-bottom: 1px solid var(--border-default);
        }
        .trodo-chat-md td {
          font-size: 12px;
          color: var(--text-primary);
          border-bottom: 1px solid var(--border-subtle);
        }
        .trodo-chat-md tr:last-child td { border-bottom: none; }

        /* Block quote — quiet, left rule */
        .trodo-chat-md blockquote {
          margin: 12px 0;
          padding: 4px 0 4px 14px;
          border-left: 2px solid ${theme.borderMuted};
          color: ${theme.textSecondary};
          font-style: normal;
        }
        .trodo-chat-md blockquote p { margin: 0; }

        /* Horizontal rule — subtle separator */
        .trodo-chat-md hr {
          border: none;
          border-top: 1px solid ${theme.borderMuted};
          margin: 16px 0;
        }
        /* The headline is markdown too: it keeps the headline's size. */
        .trodo-answer-headline .trodo-chat-md { font-size: inherit; line-height: inherit; }
        /* The source a citation was clicked from, for a moment. */
        .trodo-answer-source { transition: background-color 0.4s ease; }
        .trodo-answer-source[data-flash] { background-color: var(--surface-sunken); }
`;
let chatMdStyleInjected = false;
function ensureChatMdStyle() {
  if (chatMdStyleInjected || typeof document === "undefined") return;
  chatMdStyleInjected = true;
  const el = document.createElement("style");
  el.setAttribute("data-trodo", "chat-md");
  el.textContent = CHAT_MD_CSS;
  document.head.appendChild(el);
}

const CITE_LINK_RE = /\[(\d{1,3})\]\((trodo:\/\/(?:cite|entity)\/[^\s)]+)(?:\s+"((?:[^"\\]|\\.)*)")?\)/g;
const CITE_RUN_MIN = 4;

/**
 * Four or more citations side by side are one chip, "2–8": a finding that
 * rested on seven claims carried seven superscripts in a row, a fence the
 * reader had to read past (29 Oct). Hovering the chip lists every claim; a
 * click opens the Sources list at the first.
 */
export function collapseCitationRuns(text) {
  const src = String(text || "");
  const hits = [...src.matchAll(CITE_LINK_RE)];
  if (hits.length < CITE_RUN_MIN) return src;
  const runs = [];
  let run = [hits[0]];
  for (let i = 1; i < hits.length; i += 1) {
    const prev = run[run.length - 1];
    const gap = src.slice(prev.index + prev[0].length, hits[i].index);
    if (/^\s*$/.test(gap)) run.push(hits[i]);
    else { runs.push(run); run = [hits[i]]; }
  }
  runs.push(run);
  let out = "";
  let at = 0;
  for (const r of runs) {
    if (r.length < CITE_RUN_MIN) continue;
    const nums = r.map((m) => Number(m[1]));
    const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
    // "2 +4" (2 and four more), never "2+4", which reads as a sum.
    const label = contiguous ? `${nums[0]}–${nums[nums.length - 1]}` : `${nums[0]}\u2009+${nums.length - 1}`;
    const titles = r.map((m) => `${m[1]}. ${(m[3] || "").replace(/\\"/g, '"')}`).join("  ·  ").replace(/"/g, "'");
    const start = r[0].index;
    const end = r[r.length - 1].index + r[r.length - 1][0].length;
    out += src.slice(at, start) + `[${label}](trodo://cite/${nums[0]} "${titles}")`;
    at = end;
  }
  return out + src.slice(at);
}

const AnswerMarkdown = memo(function AnswerMarkdown({ text }) {
  ensureChatMdStyle();
  const body = useMemo(() => collapseCitationRuns(text ?? ""), [text]);

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  const markdownComponents = useMemo(
    () => ({
      // In-app entity references → subtle themed link. NOT bold, NOT loud
      // underline. Color is the brand primary at normal weight; underline
      // appears on hover only. Click opens the entity drawer in-chat (handled
      // here AND via the document-level delegation on ChatPage so widget
      // links work the same way).
      a: ChatMarkdownLink,
      pre: ({ children }) => {
        const arr = Children.toArray(children);
        if (arr.length === 0) return null;
        if (arr.length === 1 && arr[0] == null) return null;
        return (
          <pre className="trodo-chat-pre">
            {children}
          </pre>
        );
      },
      // Inline code = subtle themed chip. Used by the synth to mark up
      // important values (distinctIds, run/issue IDs, exact metrics). The
      // chip has a soft primary tint so the eye can find them without
      // shouting. Block code (``` … ```) is rendered by `pre` above.
      code: ({ inline, className, children, ...props }) => {
        if (inline) {
          return (
            <code className="trodo-chat-code-inline" {...props}>
              {children}
            </code>
          );
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
      // GFM tables + prose: bare run UUIDs and emails → trodo://entity links
      // (click delegation on ChatPage opens the in-chat drawer).
      p: ({ children, ...props }) => (
        <p {...props}>{linkifyMarkdownChildren(children)}</p>
      ),
      li: ({ children, ...props }) => (
        <li {...props}>{linkifyMarkdownChildren(children)}</li>
      ),
      td: ({ children, ...props }) => (
        <td {...props}>{linkifyMarkdownChildren(children)}</td>
      ),
    }),
    []
  );

  return (
    <div className="trodo-chat-md">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        components={markdownComponents}
        // react-markdown sanitizes hrefs and its default transform allows only
        // http/https/mailto/tel — every `trodo://entity/...` link the analyst
        // writes had its href stripped to "", so run ids rendered as inert
        // text and the `a` renderer never saw an entity. Allow OUR scheme
        // through and delegate everything else to the default, so javascript:
        // and friends stay blocked.
        urlTransform={urlTransformAllowingEntities}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
});

AnswerMarkdown.displayName = "AnswerMarkdown";

export default AnswerMarkdown;
